import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styles from './Sidebar.module.css';

const STORAGE_KEY = 'sidebar-collapsed';

const navItems = [
  {
    label: 'Home',
    path: '/dashboard',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    label: 'Calendar',
    path: '/calendar',
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
  },
  {
    label: 'Classes',
    path: '/classes',
    exact: true,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    ),
  },
  {
    label: 'Templates',
    path: '/templates',
    exact: true,
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
  },
];

const accountItem = {
  label: 'Account',
  path: '/account',
  icon: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
};

function Sidebar() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [createOpen, setCreateOpen] = useState(false);
  const createRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // localStorage unavailable
    }
  }, [collapsed]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (createRef.current && !createRef.current.contains(e.target)) {
        setCreateOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isActive = (path, exact) =>
    exact
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(path + '/');

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  const sidebarClass = [styles.sidebar, collapsed ? styles.collapsed : '']
    .filter(Boolean)
    .join(' ');

  return (
    <nav className={sidebarClass} aria-label="Main navigation">
      {/* Create button with dropdown */}
      <div className={styles.createWrapper} ref={createRef}>
        <button
          className={styles.createButton}
          onClick={() => setCreateOpen(prev => !prev)}
          title={collapsed ? 'Create' : undefined}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span className={styles.navLabel}>Create</span>
        </button>
        {createOpen && (
          <div className={styles.createDropdown}>
            <Link to="/lessons/new" className={styles.createOption} onClick={() => setCreateOpen(false)}>
              New Lesson
            </Link>
            <Link to="/worksheets/new" className={styles.createOption} onClick={() => setCreateOpen(false)}>
              New Worksheet
            </Link>
            <Link to="/templates/new" className={styles.createOption} onClick={() => setCreateOpen(false)}>
              New Template
            </Link>
            <Link to="/classes/new" className={styles.createOption} onClick={() => setCreateOpen(false)}>
              New Class
            </Link>
          </div>
        )}
      </div>

      {/* Top navigation items */}
      <div className={styles.navSection}>
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`${styles.navItem} ${isActive(item.path, item.exact) ? styles.active : ''}`}
            title={collapsed ? item.label : undefined}
          >
            <span className={styles.navIcon}>{item.icon}</span>
            <span className={styles.navLabel}>{item.label}</span>
          </Link>
        ))}
      </div>

      {/* Bottom section: Account + Collapse */}
      <div className={styles.bottomSection}>
        <Link
          to={accountItem.path}
          className={`${styles.navItem} ${isActive(accountItem.path) ? styles.active : ''}`}
          title={collapsed ? accountItem.label : undefined}
        >
          <span className={styles.navIcon}>{accountItem.icon}</span>
          <span className={styles.navLabel}>{accountItem.label}</span>
        </Link>

        <button
          className={styles.collapseButton}
          onClick={toggleCollapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <span className={styles.chevron}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </span>
          <span className={styles.navLabel}>
            {collapsed ? 'Expand' : 'Collapse'}
          </span>
        </button>
      </div>
    </nav>
  );
}

export default Sidebar;
